import React, { createContext, useContext, useState, ReactNode } from "react";

export interface IActionNotification {
  id: string;
  title: string;
  status: "running" | "completed" | "error";
  message?: string;
  timestamp: Date;
}

interface IActionNotificationContext {
  notifications: IActionNotification[];
  hasActiveNotifications: boolean;
  addNotification: (notification: Omit<IActionNotification, "id" | "timestamp">) => string;
  updateNotification: (id: string, updates: Partial<IActionNotification>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const ActionNotificationContext = createContext<IActionNotificationContext | undefined>(undefined);

interface ActionNotificationProviderProps {
  children: ReactNode;
}

export const ActionNotificationProvider: React.FC<ActionNotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<IActionNotification[]>([]);

  const hasActiveNotifications = notifications.some((n) => n.status === "running") || notifications.length > 0;

  const addNotification = (notification: Omit<IActionNotification, "id" | "timestamp">) => {
    const id = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: IActionNotification = {
      ...notification,
      id,
      timestamp: new Date(),
    };

    setNotifications((prev) => [...prev, newNotification]);
    return id;
  };

  const updateNotification = (id: string, updates: Partial<IActionNotification>) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, ...updates, timestamp: new Date() } : notification,
      ),
    );
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const value: IActionNotificationContext = {
    notifications,
    hasActiveNotifications,
    addNotification,
    updateNotification,
    removeNotification,
    clearAllNotifications,
  };

  return <ActionNotificationContext.Provider value={value}>{children}</ActionNotificationContext.Provider>;
};

export const useActionNotifications = () => {
  const context = useContext(ActionNotificationContext);
  if (context === undefined) {
    throw new Error("useActionNotifications must be used within an ActionNotificationProvider");
  }
  return context;
};
